import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/lib/store/authStore";
import { getERPClient } from "@/lib/erp/client";

const DOCTYPE = "Storefront Payment Slip";

export interface PaymentSlip {
  name: string;
  sales_order: string;
  slip_image: string;
  uploaded_at: string;
  status: "Pending" | "Verified" | "Rejected" | "Uploaded";
  verified_by?: string;
  verified_at?: string;
  rejection_reason?: string;
  company?: string;
  creation?: string;
  modified?: string;
  owner?: string;
  // EasySlip verification fields (Custom Fields via patch)
  easyslip_status?: string;
  trans_ref?: string;
  verified_amount?: number;
  sender_bank?: string;
  sender_name?: string;
  receiver_bank?: string;
  receiver_name?: string;
  verification_note?: string;
}

/**
 * List payment slips for a specific Sales Order.
 * Used by the SO detail sidebar to show linked slips inline.
 */
export function usePaymentSlipsByOrder(salesOrder: string | undefined) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery<PaymentSlip[]>({
    queryKey: ["list", DOCTYPE, "by-order", salesOrder],
    queryFn: async () => {
      const client = getERPClient();
      return client.list<PaymentSlip>({
        doctype: DOCTYPE,
        fields: ["*"],
        filters: { sales_order: salesOrder! },
        order_by: "creation desc",
      });
    },
    enabled: isAuthenticated && !!salesOrder,
    // Poll every 15s — slips may be uploaded from the storefront (cross-origin),
    // so the dashboard has no mutation event to trigger cache invalidation.
    refetchInterval: 15_000,
  });
}

/**
 * List payment slips, optionally filtered by status.
 * Uses fields: ["*"] to fetch all fields (DocType has no in_list_view fields).
 * No manual company filter — tenant_isolation.py handles it globally.
 */
export function usePaymentSlips(statusFilter?: string) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const filters: Record<string, string> = {};
  if (statusFilter) filters.status = statusFilter;

  return useQuery<PaymentSlip[]>({
    queryKey: ["list", DOCTYPE, statusFilter ?? "all"],
    queryFn: async () => {
      const client = getERPClient();
      return client.list<PaymentSlip>({
        doctype: DOCTYPE,
        fields: ["*"],
        filters: Object.keys(filters).length > 0 ? filters : undefined,
        order_by: "creation desc",
      });
    },
    enabled: isAuthenticated,
  });
}

/**
 * Verify a payment slip via the whitelisted admin method.
 * Invalidates both list and detail queries on success.
 */
export function useVerifySlip() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; status: string; slip_name: string; verified_by: string; verified_at: string },
    Error,
    string
  >({
    mutationFn: async (slipName) => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.admin_payment.verify_payment_slip",
        { slip_name: slipName }
      );
    },
    onSuccess: (_data, slipName) => {
      queryClient.invalidateQueries({ queryKey: ["list", DOCTYPE] });
      queryClient.invalidateQueries({ queryKey: ["get", DOCTYPE, slipName] });
      // Verifying creates a PE — SO advance_paid changes, auto-invoice may fire
      queryClient.invalidateQueries({ queryKey: ["sales-orders", "list"] });
      queryClient.invalidateQueries({ queryKey: ["sales-order"] });
      queryClient.invalidateQueries({ queryKey: ["linked-invoice"] });
    },
  });
}

/**
 * Reject a payment slip via the whitelisted admin method.
 * Invalidates both list and detail queries on success.
 */
export function useRejectSlip() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; status: string; slip_name: string },
    Error,
    { slipName: string; reason: string }
  >({
    mutationFn: async ({ slipName, reason }) => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.admin_payment.reject_payment_slip",
        { slip_name: slipName, reason }
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["list", DOCTYPE] });
      queryClient.invalidateQueries({
        queryKey: ["get", DOCTYPE, variables.slipName],
      });
      // Rejecting may affect order status display
      queryClient.invalidateQueries({ queryKey: ["sales-order"] });
    },
  });
}

/**
 * Approve an uploaded payment slip (skip-verify flow).
 * Submits the Draft PE and updates slip to "Verified".
 * Invalidates list, detail, and sales order queries on success.
 */
export function useApprovePayment() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; status: string; slip_name: string; verified_by: string; verified_at: string; payment_entry?: string; payment_entry_warning?: string },
    Error,
    string
  >({
    mutationFn: async (slipName) => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.admin_payment.approve_uploaded_payment",
        { slip_name: slipName }
      );
    },
    onSuccess: (_data, slipName) => {
      queryClient.invalidateQueries({ queryKey: ["list", DOCTYPE] });
      queryClient.invalidateQueries({ queryKey: ["get", DOCTYPE, slipName] });
      // Approving submits the PE — SO advance_paid changes, auto-invoice may fire
      queryClient.invalidateQueries({ queryKey: ["sales-orders", "list"] });
      queryClient.invalidateQueries({ queryKey: ["sales-order"] });
      queryClient.invalidateQueries({ queryKey: ["linked-invoice"] });
    },
  });
}

/**
 * Staff uploads a slip and marks it verified in one step.
 * Bypasses EasySlip — used from the Sales Order detail page.
 */
export function useStaffUploadSlip() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; slip_name: string; status: string; payment_entry?: string; payment_entry_warning?: string },
    Error,
    { salesOrder: string; file: File; note?: string }
  >({
    mutationFn: async ({ salesOrder, file, note }) => {
      const client = getERPClient();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("sales_order", salesOrder);
      if (note) formData.append("note", note);

      return client.callWithFormData(
        "zaviago_backend.storefront.admin_payment.staff_upload_slip",
        formData,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["list", DOCTYPE] });
      // Refresh SO detail + list + linked invoice so banner and list update immediately
      queryClient.invalidateQueries({ queryKey: ["sales-order", variables.salesOrder] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders", "list"] });
      queryClient.invalidateQueries({ queryKey: ["linked-invoice", variables.salesOrder] });
    },
  });
}

/**
 * Verify a staff-uploaded slip via EasySlip API.
 * Calls the backend which runs EasySlip verification and creates PE if it passes.
 */
export function useVerifySlipEasyslip() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; verified: boolean; status: string; slip_name: string; easyslip_status?: string; verification_note?: string; payment_entry?: string; payment_entry_warning?: string },
    Error,
    string
  >({
    mutationFn: async (slipName) => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.admin_payment.verify_staff_slip_easyslip",
        { slip_name: slipName }
      );
    },
    onSuccess: (_data, slipName) => {
      queryClient.invalidateQueries({ queryKey: ["list", DOCTYPE] });
      queryClient.invalidateQueries({ queryKey: ["get", DOCTYPE, slipName] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders", "list"] });
      queryClient.invalidateQueries({ queryKey: ["sales-order"] });
      queryClient.invalidateQueries({ queryKey: ["linked-invoice"] });
    },
  });
}

/**
 * Format a date string as relative time (e.g. "5m ago", "2h ago").
 * Shared utility for payment slip UI components.
 */
export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}
